import type { NavigateFunction } from 'react-router-dom';
import {
  Plus,
  UserPlus,
  LogOut,
  Keyboard,
  User,
  Building2,
  CreditCard,
  Users,
  Terminal,
  Package,
} from 'lucide-react';
import { REGULAR_NAV_CONFIG, ADMIN_NAV_CONFIG, type NavItemConfig } from './navigation';
import { APP_ROUTES } from './routes';
import { RequiresOrgRule, RequiresPermissionRule, CompositeRule } from '../lib/navigation/rules';
import type { Command } from '../types/commands';
import { createLogger } from '../lib/utils/logger';

/**
 * Generate navigation commands from existing nav config
 * Recursively processes nav items and nested items
 *
 * @param navigate - React Router navigate function
 */
function generateNavCommands(navigate: NavigateFunction): Command[] {
  const commands: Command[] = [];

  const processNavItem = (item: NavItemConfig, prefix = ''): void => {
    // Skip items without paths (section headers)
    if (!item.path) {
      // Process nested items with prefix
      if (item.items) {
        const sectionPrefix = `${item.label} → `;
        item.items.forEach((nested) => processNavItem(nested, sectionPrefix));
      }
      return;
    }

    // Convert to command (item.path is guaranteed to exist here)
    const itemPath = item.path;
    commands.push({
      id: `nav-${itemPath.replace(/\//g, '-')}`,
      label: prefix + item.label,
      keywords: [item.label.toLowerCase()],
      group: 'navigation',
      icon: item.icon,
      action: () => navigate(itemPath),
      visibilityRules: item.visibilityRules,
    });

    // Process nested items
    if (item.items) {
      item.items.forEach((nested) => processNavItem(nested, prefix));
    }
  };

  // Process regular nav
  REGULAR_NAV_CONFIG.forEach((item) => processNavItem(item));

  // Process admin nav with "Admin: " prefix
  ADMIN_NAV_CONFIG.forEach((item) => processNavItem(item, 'Admin: '));

  return commands;
}

/**
 * Static action commands for common user actions
 *
 * @param navigate - React Router navigate function
 * @param openInviteModal - Callback to open invite modal
 */
export function getActionCommands(
  navigate: NavigateFunction,
  openInviteModal: () => void,
): Command[] {
  return [
    {
      id: 'action-create-workflow',
      label: 'Create Workflow',
      keywords: ['new', 'workflow', 'create', 'add'],
      group: 'actions',
      icon: Plus,
      action: () => navigate(`${APP_ROUTES.WORKFLOWS}?action=create`),
      visibilityRules: [new RequiresOrgRule()],
    },
    {
      id: 'action-create-component',
      label: 'Create Task Template',
      keywords: ['new', 'component', 'create', 'add'],
      group: 'actions',
      icon: Plus,
      action: () => navigate(`${APP_ROUTES.TASK_LIBRARY}?action=create`),
      visibilityRules: [new RequiresOrgRule()],
    },
    {
      id: 'action-invite-user',
      label: 'Invite Team Member',
      keywords: ['invite', 'user', 'team', 'member', 'add'],
      group: 'actions',
      icon: UserPlus,
      action: openInviteModal,
      visibilityRules: [
        new CompositeRule(
          [new RequiresOrgRule(), new RequiresPermissionRule(['users:create'])],
          'AND',
        ),
      ],
    },
  ];
}

/**
 * Static settings/help commands
 *
 * @param navigate - React Router navigate function
 * @param openKeyboardShortcuts - Callback to open keyboard shortcuts dialog
 * @param logout - Callback to sign out user
 */
export function getSettingsCommands(
  navigate: NavigateFunction,
  openKeyboardShortcuts: () => void,
  logout: () => void,
): Command[] {
  return [
    {
      id: 'help-shortcuts',
      label: 'Keyboard Shortcuts',
      keywords: ['help', 'shortcuts', 'keys'],
      group: 'settings',
      icon: Keyboard,
      action: openKeyboardShortcuts,
    },
    {
      id: 'settings-profile',
      label: 'Profile Settings',
      keywords: ['profile', 'settings', 'preferences'],
      group: 'settings',
      icon: User,
      action: () => navigate(APP_ROUTES.SETTINGS_PROFILE),
    },
    {
      id: 'settings-organization',
      label: 'Organization Settings',
      keywords: ['organization', 'settings', 'org'],
      group: 'settings',
      icon: Building2,
      action: () => navigate(APP_ROUTES.SETTINGS_ORGANIZATION),
      visibilityRules: [
        new CompositeRule(
          [new RequiresOrgRule(), new RequiresPermissionRule(['organizations:update'])],
          'AND',
        ),
      ],
    },
    {
      id: 'settings-team',
      label: 'Team Settings',
      keywords: ['team', 'settings', 'members'],
      group: 'settings',
      icon: Users,
      action: () => navigate(APP_ROUTES.SETTINGS_TEAM),
      visibilityRules: [
        new CompositeRule(
          [new RequiresOrgRule(), new RequiresPermissionRule(['organizations:update'])],
          'AND',
        ),
      ],
    },
    {
      id: 'settings-billing',
      label: 'Billing Settings',
      keywords: ['billing', 'settings', 'subscription'],
      group: 'settings',
      icon: CreditCard,
      action: () => navigate(APP_ROUTES.SETTINGS_BILLING),
      visibilityRules: [
        new CompositeRule(
          [new RequiresOrgRule(), new RequiresPermissionRule(['organizations:billing'])],
          'AND',
        ),
      ],
    },
    {
      id: 'action-sign-out',
      label: 'Sign Out',
      keywords: ['logout', 'sign out', 'exit'],
      group: 'settings',
      icon: LogOut,
      action: logout,
    },
  ];
}

/**
 * Get all static commands (navigation + actions + settings)
 *
 * @param navigate - React Router navigate function
 * @param callbacks - Action callbacks for commands
 */
export function getAllStaticCommands(
  navigate: NavigateFunction,
  callbacks: {
    openInviteModal: () => void;
    openKeyboardShortcuts: () => void;
    logout: () => void;
  },
): Command[] {
  return [
    ...generateNavCommands(navigate),
    ...getActionCommands(navigate, callbacks.openInviteModal),
    ...getSettingsCommands(navigate, callbacks.openKeyboardShortcuts, callbacks.logout),
  ];
}

/**
 * Dynamic search for workflows
 *
 * @param query - Search query
 * @param navigate - React Router navigate function
 */
export async function searchWorkflows(
  query: string,
  navigate: NavigateFunction,
): Promise<Command[]> {
  // Import API client dynamically to avoid circular dependencies
  const { userApiClient } = await import('../lib/api/client');
  const logger = createLogger({ module: 'commands' });

  try {
    const searchParams = new URLSearchParams({
      search: query,
      per_page: '5',
    });
    const workflows = await userApiClient.get<
      Array<{ id: string; name: string; description?: string }>
    >(`/api/v1/workflows/?${searchParams.toString()}`);

    return workflows.map((wf) => ({
      id: `workflow-${wf.id}`,
      label: wf.name,
      keywords: wf.description ? [wf.description] : [],
      group: 'workflows' as const,
      icon: Terminal,
      action: () => navigate(`${APP_ROUTES.WORKFLOWS}/${wf.id}`),
    }));
  } catch (error) {
    logger.error('Failed to search workflows', error);
    return [];
  }
}

/**
 * Dynamic search for components
 *
 * @param query - Search query
 * @param navigate - React Router navigate function
 */
export async function searchComponents(
  query: string,
  navigate: NavigateFunction,
): Promise<Command[]> {
  const { userApiClient } = await import('../lib/api/client');
  const logger = createLogger({ module: 'commands' });

  try {
    const searchParams = new URLSearchParams({
      search: query,
      per_page: '5',
    });
    const components = await userApiClient.get<
      Array<{ id: string; name: string; description?: string }>
    >(`/api/v1/components/?${searchParams.toString()}`);

    return components.map((comp) => ({
      id: `component-${comp.id}`,
      label: comp.name,
      keywords: comp.description ? [comp.description] : [],
      group: 'components' as const,
      icon: Package,
      action: () => navigate(`${APP_ROUTES.TASK_LIBRARY}/${comp.id}`),
    }));
  } catch (error) {
    logger.error('Failed to search components', error);
    return [];
  }
}
